/* Admin Center — Departments tab (extracted from AdminPage.tsx) */

import { useState, useEffect } from 'react';
import { ArrowLeft, GripVertical, Trash2 } from 'lucide-react';
import { ColorPickerPopover } from '../../components/ui/color-picker';
import { Icon } from '../../components/ui/Icon';
import EmptyState from '../../components/EmptyState';
import { DEPARTMENT_COLORS } from '../../lib/palette';
import { rem } from '../../lib/rem';
import { DepartmentService } from '../../services/departmentService';
import type { Department } from '../../../shared/entities';
import { useTableSync } from '../../hooks/useTableSync';
import type { AdminPageProps } from './types';
import {
  inputStyle,
  btnPrimary,
  btnDanger,
  DRAG_HANDLE_STYLE,
  useDragReorder,
  RowSkeleton,
} from './shared';

/* ═══════════ DEPARTMENTS TAB ═══════════
   Department lookup with name + abbreviation + color picker.
   Drag-orderable like the other lookups; the order set here drives
   forecast-grid grouping order and team-page sort. */
function DepartmentsTab({ toast }: { toast: AdminPageProps['toast'] }) {
  const [items, setItems] = useState<Department[]>([]);
  const [editing, setEditing] = useState<Department | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const list = await DepartmentService.list();
      setItems(list);
    } catch (err: any) {
      toast('Load failed: ' + err.message, true);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);
  useTableSync(['departments'], () => load());

  function empty(): Department {
    return {
      id: '',
      name: '',
      abbreviation: '',
      color: DEPARTMENT_COLORS[items.length % DEPARTMENT_COLORS.length],
      sortOrder: items.length,
    };
  }

  async function save() {
    if (!editing || saving) return;
    if (!editing.name.trim()) {
      toast('Enter a department name', true);
      return;
    }
    setSaving(true);
    try {
      await DepartmentService.save(editing);
      setEditing(null);
      await load();
      toast('Department saved');
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (
      !confirm('Delete this department? Members assigned to it will have their department cleared.')
    )
      return;
    try {
      await DepartmentService.delete(id);
      if (editing?.id === id) setEditing(null);
      await load();
      toast('Department deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  const drag = useDragReorder(items, async (reordered) => {
    setItems(reordered);
    try {
      await Promise.all(
        reordered.map((it, i) => {
          if ((items.find((x) => x.id === it.id)?.sortOrder ?? -1) !== i) {
            return DepartmentService.save({ ...it, sortOrder: i });
          }
          return Promise.resolve();
        })
      );
    } catch (err: any) {
      toast('Reorder failed: ' + err.message, true);
      await load();
    }
  });

  if (editing) {
    return (
      <div style={{ maxWidth: rem(540) }}>
        <button
          onClick={() => setEditing(null)}
          style={{
            ...btnDanger,
            display: 'inline-flex',
            alignItems: 'center',
            gap: rem(6),
            marginBottom: 'var(--space-3)',
            color: 'var(--tm)',
            border: '1px solid var(--bd)',
          }}
        >
          <Icon icon={ArrowLeft} em={1.0833} /> Back
        </button>
        <h3 style={{ fontSize: rem(15), fontWeight: 700, margin: `0 0 var(--space-4)` }}>
          {editing.id ? 'Edit Department' : 'New Department'}
        </h3>

        <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
          <label style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Department Name
            </div>
            <input
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              style={inputStyle}
              placeholder="e.g. Mechanical, Electrical, Plumbing & Tech"
            />
          </label>
          <label style={{ width: rem(120) }}>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Abbreviation
            </div>
            <input
              value={editing.abbreviation || ''}
              onChange={(e) => setEditing({ ...editing, abbreviation: e.target.value })}
              style={{ ...inputStyle, textTransform: 'uppercase' }}
              maxLength={6}
              placeholder="MEPT"
            />
          </label>
        </div>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--tm)',
              marginBottom: rem(6),
            }}
          >
            Color
          </div>
          <ColorPickerPopover
            value={editing.color || '#888888'}
            onChange={(hex) => setEditing({ ...editing, color: hex })}
            swatches={DEPARTMENT_COLORS}
            allowAlpha={false}
            aria-label="Department color"
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={save}
            disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Save Department'}
          </button>
          {editing.id && (
            <button onClick={() => remove(editing.id)} style={btnDanger}>
              Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
          Departments group people on the team page and the forecasting grid (e.g. MEPT, BIM,
          Architecture). Drag rows to reorder.
        </div>
        <button onClick={() => setEditing(empty())} style={btnPrimary}>
          + Add Department
        </button>
      </div>

      {loading ? (
        <RowSkeleton label="Loading departments" />
      ) : (
        <div
          style={{
            background: 'var(--sf)',
            borderRadius: rem(8),
            border: '1px solid var(--bd)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '40px 32px 110px 1fr 80px',
              gap: 'var(--space-3)',
              alignItems: 'center',
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--sa)',
              borderBottom: '1px solid var(--bd)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            <span></span>
            <span></span>
            <span>Abbreviation</span>
            <span>Department</span>
            <span></span>
          </div>

          {items.map((d, i) => {
            const isDragging = drag.dragIdx === i;
            const isDropTarget = drag.overIdx === i && drag.dragIdx !== null && drag.dragIdx !== i;
            return (
              <div
                key={d.id}
                {...drag.rowProps(i)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 32px 110px 1fr 80px',
                  gap: 'var(--space-3)',
                  alignItems: 'center',
                  padding: `${rem(10)} var(--space-3)`,
                  borderBottom: '1px solid var(--bd)',
                  background: isDragging
                    ? 'var(--sa)'
                    : isDropTarget
                      ? 'rgba(var(--acr),0.08)'
                      : '',
                  opacity: isDragging ? 0.5 : 1,
                  transition: 'background .1s',
                  cursor: 'pointer',
                }}
                onClick={() => setEditing({ ...d })}
              >
                <span
                  {...drag.handleProps()}
                  style={DRAG_HANDLE_STYLE}
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                >
                  <Icon icon={GripVertical} em={14 / 16} />
                </span>
                <span
                  style={{
                    width: rem(20),
                    height: rem(20),
                    borderRadius: rem(5),
                    background: d.color || 'var(--bd)',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    fontWeight: 700,
                    color: 'var(--tx)',
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: '.02em',
                  }}
                >
                  {d.abbreviation || '—'}
                </span>
                <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
                  {d.name}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(d.id);
                  }}
                  style={btnDanger}
                  title="Delete department"
                  aria-label={`Delete department ${d.name}`}
                >
                  <Icon icon={Trash2} em={14 / 12} />
                </button>
              </div>
            );
          })}

          {items.length === 0 && (
            <EmptyState
              title="No departments yet"
              subtitle='Click "Add Department" to create the first.'
            />
          )}
        </div>
      )}
    </div>
  );
}

export { DepartmentsTab };
